import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  createOrganization,
  createOrganizationLink,
  disableOrganizationLink,
  enableOrganizationLink,
  resetOrganizationLink,
  toggleOrganizationActive,
  updateOrganization,
  type OrganizationLinkResponse,
} from "./organizationApi";
import { organizationKeys } from "./organization.keys";
import {
  CreateOrganizationCommand,
  OrganizationDto,
  OrganizationLinkDto,
  ToggleStatusRequest,
  UpdateOrganizationCommand,
} from "../../web-api-client";

export function useCreateOrganization() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CreateOrganizationCommand) =>
      createOrganization(payload),
    onSuccess: (data: OrganizationDto) => {
      invalidateOrganizationLists(queryClient);
      if (data?.id) {
        invalidateOrganizationDetail(queryClient, data.id);
      }
    },
  });
}

export function useUpdateOrganization() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: number;
      payload: UpdateOrganizationCommand;
    }) => updateOrganization(id, payload),
    onSuccess: (data, variables) => {
      invalidateOrganizationLists(queryClient);
      invalidateOrganizationDetail(queryClient, data?.id ?? variables.id);
    },
  });
}

export function useToggleOrganizationActive() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: number;
      payload: ToggleStatusRequest;
    }) => toggleOrganizationActive(id, payload),
    onSuccess: (data, variables) => {
      invalidateOrganizationLists(queryClient);
      invalidateOrganizationDetail(queryClient, data?.id ?? variables.id);
    },
  });
}

export function useCreateOrganizationLink() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => createOrganizationLink(id),
    onSuccess: (_data: OrganizationLinkResponse, id) => {
      invalidateOrganizationLists(queryClient);
      invalidateOrganizationDetail(queryClient, id);
    },
  });
}

export function useResetOrganizationLink() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => resetOrganizationLink(id),
    onSuccess: (_data: OrganizationLinkResponse, id) => {
      invalidateOrganizationLists(queryClient);
      invalidateOrganizationDetail(queryClient, id);
    },
  });
}

export function useSetOrganizationLinkEnabled() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      enabled,
    }: {
      id: number;
      enabled: boolean;
    }) => (enabled ? enableOrganizationLink(id) : disableOrganizationLink(id)),
    onSuccess: (_data: OrganizationLinkDto, variables) => {
      invalidateOrganizationLists(queryClient);
      invalidateOrganizationDetail(queryClient, variables.id);
    },
  });
}

function invalidateOrganizationLists(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries({ queryKey: organizationKeys.listBase() });
}

function invalidateOrganizationDetail(
  queryClient: ReturnType<typeof useQueryClient>,
  id: number
) {
  queryClient.invalidateQueries({ queryKey: organizationKeys.statsBase(id) });
  queryClient.invalidateQueries({
    queryKey: organizationKeys.appointmentsBase(id),
  });
}